import * as React from 'react';
import BPromise from 'bluebird';
import Lazy from 'lazy.js';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { parse } from 'query-string';
import { getOrder, getSearchFilter, getSearchOrder, getServerFieldSectionWhere } from 'Src/helpers/table/table';
import {
  ColumnDefinition,
  getFilters2,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { History, Location } from 'history';
import { Grid2 } from 'Src/components/Grid/Grid2';
import { Loader } from 'Src/components/Loader/Loader';
import { DATE_INTERVAL, DEFAULT_LIMIT, DEFAULT_SKIP, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import { getTournament, getTournamentAthleteSchools } from 'Src/helpers/service/adminSU/tournamentsTable';
import { Tournament } from 'Src/models/tournament';
import { getTeamGender } from 'Src/helpers/accessor/accessor';
import { formatTournamentAges } from 'Src/helpers/ageGroups';
import { getSelectOptionForTournamentTeamAges, getSelectOptionForTournamentTeamGender } from 'Src/helpers/table/select';
import {
  getAllSchoolUnionHighLevelTournamentTeams,
  getSchoolUnionHighLevelTournamentTeams,
  getSchoolUnionHighLevelTournamentTeamsCount
} from 'Src/helpers/service/adminSU/tournamentTeamsTable';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import {
  addSchoolTournamentTeam,
  editSchoolTournamentTeam,
  removeSchoolTournamentTeam
} from 'Src/helpers/service/admin/tournamentTeams';
import { TEAM_TYPE } from 'Src/consts/team';
import { TournamentTeam } from 'Src/models/tournamentTeam';
import { TournamentTeamForm as HighLevelTournamentTeamForm } from '../../../AdminView/Tournaments/TournamentTeamForm/TournamentTeamForm';
import { isIndividualTournament } from 'Src/helpers/tournament/tournament';
import '../HighLevelTournaments.scss';
import '../../Tournaments/Tournaments.css';
import '../../../AdminView/Invites/SchoolInvitesHub.scss';
import './HighLevelTournamentTeams.scss';

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'School Name',
    field: 'schoolName',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['school', 'name']
  },
  {
    text: 'Team Name',
    field: 'name',
    isSort: false,
    type: FILTER_TYPE.TEXT,
    accessor: ['name']
  },
  {
    text: 'Gender',
    field: 'gender',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getTeamGender
  },
  {
    text: 'Age',
    field: 'teamAges',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: (team: TournamentTeam) => formatTournamentAges(team.ages) || '—'
  }
];

interface State {
  items: TournamentTeam[];
  currentPage: number;
  selectedItems: TournamentTeam[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  filters: any;
  isDataFiltered: boolean;
  isLoading: boolean;
  total: number;
  basePath: string;
  isSelectAllChecked: boolean;
  tournament: Tournament;
  isAddTeamModalOpen: boolean;
  isEditTeamModalOpen: boolean;

  isDeleteConfirmWindowOpen: boolean;
  isTeamFormWindowOpen: boolean;
  isDeleteAlertWindowOpen: boolean;
  allTournamentTeams: TournamentTeam[];
  result: any;
  teamCount: number;
  athleteCount: number;
  athleteSchoolCount: number;
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
  embedded?: boolean;
  participationReadOnly?: boolean;
}

export class HighLevelTournamentTeams extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      filters: {},
      isDataFiltered: false,
      isLoading: false,
      total: 0,
      basePath: '',
      isSelectAllChecked: false,
      tournament: undefined,
      isAddTeamModalOpen: false,
      isEditTeamModalOpen: false,

      isDeleteConfirmWindowOpen: false,
      isTeamFormWindowOpen: false,
      isDeleteAlertWindowOpen: false,
      allTournamentTeams: [],
      result: undefined,
      teamCount: 0,
      athleteCount: 0,
      athleteSchoolCount: 0
    };
  }

  componentDidMount() {
    this.setState({
      isLoading: true
    });

    this.setAdditionalItems().then(res => {
      this.setItems();
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({
        isLoading: true
      });
      this.setItems(nextProps);
    }
  }

  getTournamentId(props: Props = this.props): string {
    const { history } = props;
    const search = parse(history.location.search);
    const tournamentId = search.tournamentId;
    return tournamentId;
  }

  setAdditionalItems() {
    const { user } = this.props;

    const tournamentId = this.getTournamentId();

    return BPromise.all([
      getTournament(user, tournamentId),
      getTournamentAthleteSchools(user, tournamentId, false),
      getSchoolUnionHighLevelTournamentTeamsCount(user, tournamentId)
    ]).then(([tournament, athleteSchools, teamsCount]) => {
      const schools = Array.isArray(athleteSchools) ? athleteSchools : [];
      this.setState({
        tournament: tournament,
        teamCount: Number(teamsCount && teamsCount.count) || 0,
        athleteCount: schools.reduce((total, school) => total + Number(school.athleteCount || 0), 0),
        athleteSchoolCount: schools.length
      });

      return true;
    });
  }

  setItems(props: Props = this.props) {
    const { history, user } = props;

    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters2(COLUMNS, search);

    const isShowFilter = false;
    const isDataFiltered = isFilterExist2(filters);

    const where = getServerFieldSectionWhere(filters);

    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    const tournamentId = this.getTournamentId(props);

    const getItemsPromise = getSchoolUnionHighLevelTournamentTeams(user, tournamentId, serverQueryFilter);
    const getItemsCountPromise = getSchoolUnionHighLevelTournamentTeamsCount(user, tournamentId, where);

    return BPromise.all([getItemsCountPromise, getItemsPromise]).then(([countObj, items]) => {
      this.setState({
        currentPage: page,
        sortDirection: sortDirection,
        sortColumnsName: sortColumnsName,
        isShowFilter: isShowFilter,
        isDataFiltered: isDataFiltered,
        filters: filters,
        items: items,
        total: countObj.count,
        basePath: history.location.pathname,
        isLoading: false
      });

      return true;
    });
  }

  setCurrentPageParams = (currentPage: number): void => {
    let search: string[] = [];

    const { filters, sortColumnsName, sortDirection } = this.state;
    const { history } = this.props;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    const tournamentId = this.getTournamentId();

    this.props.history.push({
      pathname: this.state.basePath,
      search: `tournamentId=${tournamentId}${search.length ? `&${search.join('&')}` : ''}`,
      state: history.location.state
    });
  };

  onItemSelect = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      selectedItems: selectedItemsUpdated
    });
  };

  onItemClick = (index: number): void => {
    const team = this.state.items[index];
    if (!team) return;
    this.setState({ selectedItems: [team] }, this.isReadOnly() ? this.onAllocateToEventsClick : this.onEditTeamPlayersClick);
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const itemsFiltered = items.filter(item => {
      return selectedItems.every(selectedItem => selectedItem.id !== item.id);
    });

    const nextSelectedItems = [...selectedItems, ...itemsFiltered];

    this.setState({
      selectedItems: nextSelectedItems,
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;

    this.setState({
      isLoading: true
    });

    const user = this.props.user;

    const where = getServerFieldSectionWhere(filters);

    const tournamentId = this.getTournamentId();

    getAllSchoolUnionHighLevelTournamentTeams(user, tournamentId, where).then(items => {
      const itemsFiltered = items.filter(item => {
        return selectedItems.every(selectedItem => selectedItem.id !== item.id);
      });

      const nextSelectedItems = [...selectedItems, ...itemsFiltered];

      this.setState({
        selectedItems: nextSelectedItems,
        isSelectAllChecked: true,
        isLoading: false
      });
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;
    const { history } = this.props;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search: string[] = [];
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    const tournamentId = this.getTournamentId();

    this.props.history.push({
      pathname: this.state.basePath,
      search: `tournamentId=${tournamentId}${search.length ? `&${search.join('&')}` : ''}`,
      state: history.location.state
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (options && options.modernRange) {
      nextFilters = {
        ...nextFilters,
        [filterField]: options.modernRange
      };
    } else if (options && options.modernMultiValue) {
      nextFilters = {
        ...nextFilters,
        [filterField]: Array.isArray(filterValue) ? filterValue : []
      };
    } else if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue
        };
      }
    }

    this.setState({
      filters: nextFilters
    });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    const { history } = this.props;
    let search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      isShowFilter: false
    });

    const tournamentId = this.getTournamentId();

    this.props.history.push({
      pathname: this.state.basePath,
      search: `tournamentId=${tournamentId}${search.length ? `&${search.join('&')}` : ''}`,
      state: history.location.state
    });
  };

  onClearFilterClick = (): void => {
    const { history } = this.props;
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      isShowFilter: false,
      filters: {}
    });

    const tournamentId = this.getTournamentId();

    this.props.history.push({
      pathname: this.state.basePath,
      search: `tournamentId=${tournamentId}`,
      state: history.location.state
    });
  };

  onTableFilterClick = (event): void => {
    event?.preventDefault?.();

    const isShowFilter = this.state.isShowFilter;

    this.setState({
      isShowFilter: !isShowFilter
    });
  };

  onClearIndividualFilter = (filterField: string): void => {
    const { filters, sortDirection, sortColumnsName, basePath } = this.state;
    const nextFilters = { ...filters };
    delete nextFilters[filterField];

    const search: string[] = [];
    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }
    if (isFilterExist2(nextFilters)) {
      search.push(getSearchFilter(nextFilters));
    }

    const tournamentId = this.getTournamentId();
    this.setState({
      filters: nextFilters,
      selectedItems: [],
      isSelectAllChecked: false
    });
    this.props.history.push({
      pathname: basePath,
      search: `tournamentId=${tournamentId}${search.length ? `&${search.join('&')}` : ''}`,
      state: this.props.history.location.state
    });
  };

  onCancelFilterClick = (): void => {
    const appliedFilters = getFilters2(COLUMNS, parse(this.props.history.location.search));
    this.setState({
      isShowFilter: false,
      filters: appliedFilters
    });
  };

  isOneItemSelected(): boolean {
    const selectedItems = this.state.selectedItems;
    return selectedItems.length === 1;
  }

  goBack = () => {
    const { history, location } = this.props;
    const search = propz.get(location, ['state', 'search'], '');
    const tournamentId = this.getTournamentId();

    history.push({
      pathname: '/highLevelTournaments/upcoming',
      search: search,
      state: { tournamentId: tournamentId }
    });
  };

  isReadOnly = () => Boolean(this.props.participationReadOnly || this.state.tournament?.isReadOnly);

  renderGrid() {
    const {
      items,
      sortDirection,
      sortColumnsName,
      isShowFilter,
      isDataFiltered,
      selectedItems,
      filters,
      isSelectAllChecked,
      currentPage,
      total,
      tournament
    } = this.state;
    const { user, embedded } = this.props;

    const actionItems = [
      {
        itemText: 'Edit team',
        onItemClick: this.onEditTeamClick,
        isActive: selectedItems.length === 1 && !this.isReadOnly()
      },
      {
        itemText: 'Edit participants',
        onItemClick: this.onEditTeamPlayersClick,
        isActive: selectedItems.length === 1 && !this.isReadOnly()
      },
      {
        itemText: 'Allocate to events',
        onItemClick: this.onAllocateToEventsClick,
        isActive: selectedItems.length === 1 && !this.isReadOnly()
      },
      {
        itemText: selectedItems.length === 1 ? 'Delete team' : 'Delete teams',
        onItemClick: this.onDeleteTeamClick,
        isActive: selectedItems.length > 0 && !this.isReadOnly(),
        isDanger: true
      }
    ];

    const filterOptions = {
      gender: getSelectOptionForTournamentTeamGender(),
      teamAges: getSelectOptionForTournamentTeamAges(tournament, user)
    };
    const columns: ColumnDefinition[] = embedded ? COLUMNS.map(column => column.field === 'schoolName' ? {...column, text: 'Union'} : column) : COLUMNS;
    if (embedded) {
      columns.push({
      text: 'Children', field: 'children', isSort: false, type: FILTER_TYPE.NONE,
      cell: (team: TournamentTeam) => <td className="hltTeamsWorkspace__open" onClick={event => event.stopPropagation()}>
        <button type="button" className="eDsButton eDsButton--secondary" aria-label={`${this.isReadOnly() ? 'View' : 'Manage'} children in ${team.name}`}
          onClick={() => this.onItemClick(items.findIndex(item => item.id === team.id))}>{this.isReadOnly() ? 'View children' : 'Manage children'}<span aria-hidden="true">→</span></button>
      </td>
      });
    }
    return (
      <>
        {embedded ? <header className="hltTeamsWorkspace__heading">
          <div><h2>Teams &amp; children</h2><p>{this.isReadOnly() ? 'View your teams and their children. This tournament is read-only.' : 'Manage your teams, recruit children and allocate them to tournament events.'}</p></div>
          {!this.isReadOnly() && <button type="button" className="eDsButton eDsButton--primary" onClick={this.onAddTeamClick}><span aria-hidden="true">+</span> Add team</button>}
        </header> : <header className="hltTeamsPage__header">
          <button type="button" className="hltTeamsPage__back" onClick={this.goBack}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
            High-level tournaments
          </button>
          <span className="hltTeamsPage__divider" aria-hidden="true" />
          <div className="hltTeamsPage__title">
            <span>Manage teams</span>
            <h1>{tournament ? tournament.name : 'Tournament'}</h1>
          </div>
          <button type="button" className="hltTeamsPage__add" disabled={tournament?.isReadOnly} onClick={this.onAddTeamClick}>
            <span aria-hidden="true">+</span> Add team
          </button>
          <button type="button" className="eDsButton eDsButton--secondary" onClick={() => this.props.history.push(`/highLevelTournaments/availability?tournamentId=${this.getTournamentId()}`)}>Availability</button>
        </header>}
        <div className="hltTeamsPage__summary" aria-label="Team summary">
          <span><b>{this.state.teamCount}</b> team{this.state.teamCount === 1 ? '' : 's'}</span>
          <span><b>{this.state.athleteCount}</b> child{this.state.athleteCount === 1 ? '' : 'ren'}</span>
          <span><b>{this.state.athleteSchoolCount}</b> school{this.state.athleteSchoolCount === 1 ? '' : 's'} involved</span>
        </div>
        <div className={embedded ? 'hltTeamsWorkspace__table' : 'unionTournamentsModern hltTeamsPage__card'}>
          <Grid2
            dataItems={items}
            filters={filters}
            currentPage={currentPage}
            total={total}
            isSelectAllChecked={isSelectAllChecked}
            isDataFiltered={isDataFiltered}
            sortDirection={sortDirection}
            sortColumnsName={sortColumnsName}
            isShowFilter={isShowFilter}
            dataItemsSelected={selectedItems}
            columns={columns}
            actionItems={actionItems}
            options={filterOptions}
            onItemClick={this.onItemClick}
            onItemSelect={this.onItemSelect}
            onSortClick={this.onTableSortClick}
            onApplyFilterClick={this.onApplyFilterClick}
            onClearFilterClick={this.onClearFilterClick}
            onClearIndividualFilter={this.onClearIndividualFilter}
            onCancelFilterClick={this.onCancelFilterClick}
            onTableFilterChange={this.onTableFilterChange}
            onTableFilterClick={this.onTableFilterClick}
            setCurrentPageParams={this.setCurrentPageParams}
            onSelectAllClick={this.onSelectAllClick}
            onSelectAllOnPageClick={this.onSelectAllOnPageClick}
            onUnselectAllClick={this.onUnselectAllClick}
            gridTitle={`${total} team${total === 1 ? '' : 's'}`}
            toolbarSearchField="name"
            toolbarSearchPlaceholder="Search teams..."
            onToolbarSearchClear={() => this.onClearIndividualFilter('name')}
            hiddenFilterFields={['name']}
            isActionsInline
            selectionActionsBelow
            showClearSelection
            isSelectionHidden={this.isReadOnly()}
            isSelectDisabled={this.isReadOnly()}
            fixedColumnFields={embedded ? ['name', 'children'] : ['name']}
            defaultHiddenColumns={embedded ? ['schoolName'] : []}
            columnVisibilityKey={embedded ? 'union-participation-teams' : 'union-high-level-tournament-teams'}
            emptyText="No teams found"
            emptyHint="Try changing the filters or add a team."
            isCSV={false}
            stylizeCell={embedded ? ({column}) => column.field === 'name' ? 'hltTeamsWorkspace__name' : '' : undefined}
            user={user}
          />
        </div>
      </>
    );
  }

  onAddTeamFormSubmit = (data: any) => {
    const { name } = data;
    const { tournament } = this.state;
    const { user } = this.props;
    const { activeSchoolId: schoolId } = user;

    const { sportId } = tournament;
    const tournamentId = this.getTournamentId();

    this.setState({
      isLoading: true,
      isAddTeamModalOpen: false
    });

    let teamData;

    switch (true) {
      case isIndividualTournament(tournament): {
        const { gender, ages } = tournament;
        teamData = {
          name,
          ages,
          gender,
          sportId,
          schoolId,
          teamType: TEAM_TYPE.ADHOC,
          players: []
        };
        break;
      }

      default: {
        const { age, gender } = data;
        teamData = {
          name,
          gender,
          sportId,
          schoolId,
          teamType: TEAM_TYPE.ADHOC,
          players: [],
          ages: [Number(age)]
        };
      }
    }

    addSchoolTournamentTeam(user, tournamentId, teamData).then(res => {
      this.setAdditionalItems().then(res => {
        this.setItems();
      });
    });
  };

  onEditTeamFormSubmit = (data: any) => {
    const { name } = data;
    const { user } = this.props;
    const tournamentId = this.getTournamentId();

    const { selectedItems, tournament } = this.state;
    const team = selectedItems[0];
    const { id: teamId } = team;

    this.setState({
      isLoading: true,
      isEditTeamModalOpen: false
    });

    let teamData;

    switch (true) {
      case isIndividualTournament(tournament): {
        teamData = { name };
        break;
      }

      default: {
        const { age, gender } = data;
        teamData = {
          name,
          gender,
          ages: [Number(age)]
        };
      }
    }

    editSchoolTournamentTeam(user, tournamentId, teamId, teamData).then(team => {
      this.setState({
        isLoading: false,
        selectedItems: [team]
      });
      this.setAdditionalItems().then(res => {
        this.setItems();
      });
    });
  };

  renderAddTeamModal(): React.ReactNode {
    const { isAddTeamModalOpen, tournament, items } = this.state;
    const { user } = this.props;

    return (
      <SimpleModal
        isOpen={isAddTeamModalOpen}
        showHeader={false}
        showFooter={false}
        bodyClassName="tournamentTeamModalHost"
        dialogStyle={{ maxWidth: 500 }}
        onBackdropClick={this.onAddTeamFormCancel}
      >
        <HighLevelTournamentTeamForm
          tournament={tournament}
          onSubmit={this.onAddTeamFormSubmit}
          onCancel={this.onAddTeamFormCancel}
          user={user}
          allSchoolTeams={items}
        />
      </SimpleModal>
    );
  }

  onAddTeamClick = (): void => {
    if (this.isReadOnly()) return;
    this.setState({
      isAddTeamModalOpen: true
    });
  };

  onAddTeamFormCancel = (): void => {
    this.setState({
      isAddTeamModalOpen: false
    });
  };

  renderEditTeamModal(): React.ReactNode {
    const { isEditTeamModalOpen, tournament, items, selectedItems } = this.state;
    const { user } = this.props;
    const team = selectedItems[0];

    return (
      <SimpleModal
        isOpen={isEditTeamModalOpen}
        showHeader={false}
        showFooter={false}
        bodyClassName="tournamentTeamModalHost"
        dialogStyle={{ maxWidth: 500 }}
        onBackdropClick={this.onEditTeamFormCancel}
      >
        <HighLevelTournamentTeamForm
          tournament={tournament}
          onSubmit={this.onEditTeamFormSubmit}
          onCancel={this.onEditTeamFormCancel}
          user={user}
          team={team}
          allSchoolTeams={items}
        />
      </SimpleModal>
    );
  }

  onEditTeamFormCancel = (): void => {
    this.setState({
      isEditTeamModalOpen: false
    });
  };

  onEditTeamClick = (): void => {
    this.setState({
      isEditTeamModalOpen: true
    });
  };

  onEditTeamPlayersClick = () => {
    const { location } = this.props;
    const prevPath = propz.get(location, ['state', 'prevPath'], '/highLevelTournaments/upcoming');
    const { selectedItems } = this.state;
    const team = selectedItems[0];
    const { id } = team;
    const tournamentId = this.getTournamentId();

    this.props.history.push({
      pathname: `/highLevelTournaments/teams/players`,
      search: `teamId=${id}&tournamentId=${tournamentId}`,
      state: { prevPath }
    });
  };

  onAllocateToEventsClick = () => {
    const { location } = this.props;
    const { state } = location;
    const search = propz.get(state, ['search'], '');
    const { selectedItems } = this.state;
    const team = selectedItems[0];
    const { id, name } = team;
    const tournamentId = this.getTournamentId();

    this.props.history.push({
      pathname: `/highLevelTournaments/teams/players`,
      search: `teamId=${id}&tournamentId=${tournamentId}`,
      state: { search, teamName: name }
    });
  };

  onDeleteConfirmWindowOkClick = () => {
    const { selectedItems } = this.state;
    const { user } = this.props;
    const tournamentId = this.getTournamentId();

    this.setState({
      isLoading: true,
      isDeleteConfirmWindowOpen: false,
      selectedItems: [],
      isSelectAllChecked: false
    });

    BPromise.all(
      selectedItems.map(team => {
        return removeSchoolTournamentTeam(user, tournamentId, team.id);
      })
    )
      .then(() => this.setAdditionalItems())
      .then(() => this.setItems());
  };

  onDeleteConfirmWindowCloseClick = () => {
    this.setState({
      isDeleteConfirmWindowOpen: false
    });
  };

  onDeleteTeamClick = () => {
    const { selectedItems: teamsDeleted } = this.state;
    const { user } = this.props;
    const tournamentId = this.getTournamentId();

    const schoolIds = Lazy(teamsDeleted)
      .map(team => team.schoolId)
      .uniq()
      .toArray();

    this.setState({
      isLoading: true
    });

    const filter = {
      where: { schoolId: { $in: schoolIds } },
      skip: DEFAULT_SKIP,
      limit: DEFAULT_LIMIT
    };

    getSchoolUnionHighLevelTournamentTeams(user, tournamentId, filter).then(allTeams => {
      const isDeleteAvailable = schoolIds.every(schoolId => {
        const schoolTeamsDeleted = teamsDeleted.filter(team => team.schoolId === schoolId);
        const allSchoolTeams = allTeams.filter(team => team.schoolId === schoolId);
        const schoolTeamsDeletedCount = schoolTeamsDeleted.length;
        const allSchoolTeamsCount = allSchoolTeams.length;
        const diffCount = allSchoolTeamsCount - schoolTeamsDeletedCount;

        return diffCount > 0;
      });

      if (isDeleteAvailable) {
        this.setState({
          isDeleteConfirmWindowOpen: true,
          isLoading: false
        });
      } else {
        this.setState({
          isDeleteAlertWindowOpen: true,
          isLoading: false
        });
      }
    });
  };

  onDeleteAlertWindowOkClick = () => {
    this.setState({
      isDeleteAlertWindowOpen: false
    });
  };

  renderDeleteAlertWindow(): React.ReactNode {
    const { isDeleteAlertWindowOpen, tournament } = this.state;

    return (
      <SimpleModal
        isOpen={isDeleteAlertWindowOpen}
        showHeader={false}
        showFooter={false}
        bodyClassName="tournamentTeamActionModalHost"
        dialogStyle={{ maxWidth: 440 }}
        onBackdropClick={this.onDeleteAlertWindowOkClick}
      >
        <section className="tournamentTeamActionModal isWarning" role="alertdialog" aria-label="Team cannot be removed">
          <header className="tournamentTeamActionModal__head">
            <span className="tournamentTeamActionModal__icon" aria-hidden="true">!</span>
            <div>
              <h2>Team cannot be removed</h2>
              <p>This school must keep a team while it remains entered.</p>
            </div>
            <button type="button" onClick={this.onDeleteAlertWindowOkClick} aria-label="Close">×</button>
          </header>
          <div className="tournamentTeamActionModal__body">
            <div className="tournamentTeamActionModal__message">
              This is the school's only team in {tournament ? tournament.name : 'this tournament'}. To remove it,
              remove the school from the Participants list first.
            </div>
          </div>
          <footer className="tournamentTeamActionModal__foot">
            <button type="button" className="isPrimary" onClick={this.onDeleteAlertWindowOkClick}>Close</button>
          </footer>
        </section>
      </SimpleModal>
    );
  }

  renderDeleteConfirmWindow(): React.ReactNode {
    const { isDeleteConfirmWindowOpen, selectedItems } = this.state;

    return (
      <SimpleModal
        isOpen={isDeleteConfirmWindowOpen}
        title={'Confirm'}
        body={`Are you sure you want to remove this ${selectedItems.length === 1 ? 'team' : 'teams'} from tournament?`}
        buttonText={'Ok'}
        onButtonClick={this.onDeleteConfirmWindowOkClick}
        onCloseClick={this.onDeleteConfirmWindowCloseClick}
      />
    );
  }

  render() {
    const {
      isLoading,
      isDeleteConfirmWindowOpen,
      isDeleteAlertWindowOpen,
      isTeamFormWindowOpen,
      isAddTeamModalOpen,
      isEditTeamModalOpen
    } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const classes =
      isDeleteConfirmWindowOpen ||
      isTeamFormWindowOpen ||
      isDeleteAlertWindowOpen ||
      isAddTeamModalOpen ||
      isEditTeamModalOpen
        ? 'hltTeamsPage modal-open'
        : 'hltTeamsPage';

    return (
      <main className={`${classes}${this.props.embedded ? ' schoolInvitesList hltTeamsWorkspace' : ''}`} aria-label={this.props.embedded ? 'Tournament teams and children' : undefined}>
        {/*Place for render modal windows*/}
        {this.renderDeleteConfirmWindow()}
        {this.renderAddTeamModal()}
        {this.renderEditTeamModal()}
        {this.renderDeleteAlertWindow()}
        {this.renderGrid()}
      </main>
    );
  }
}


